import { useState } from "react";
import type { Contact, Customer } from "../types";
import { Modal } from "./Modal";
import { todayIso } from "../utils";

interface Props {
  title: string;
  initial?: Partial<Customer>;
  contactKinds: string[];
  onClose: () => void;
  onSubmit: (body: Partial<Customer> & { contacts?: Contact[] }) => void | Promise<void>;
}

export function CustomerForm({ title, initial, contactKinds, onClose, onSubmit }: Props) {
  const isEdit = !!initial?.id;
  const [name, setName] = useState(initial?.name ?? "");
  const [id, setId] = useState(initial?.id ?? "");
  const [channel, setChannel] = useState(initial?.channel ?? "");
  const [priceCny, setPriceCny] = useState<string>(
    initial?.price_cny != null ? String(initial.price_cny) : "",
  );
  const [paidAt, setPaidAt] = useState(
    initial?.paid_at ?? (isEdit ? "" : todayIso()),
  );
  const [fingerprint, setFingerprint] = useState(initial?.fingerprint ?? "");
  const [note, setNote] = useState(initial?.note ?? "");
  const [contacts, setContacts] = useState<Contact[]>(initial?.contacts ?? []);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const body: Partial<Customer> & { contacts?: Contact[] } = {
      name: name.trim(),
      channel: channel.trim(),
      paid_at: paidAt.trim(),
      fingerprint: fingerprint.trim(),
      note,
    };
    if (!isEdit) {
      if (id.trim()) body.id = id.trim();
      body.contacts = contacts;
    }
    if (priceCny.trim()) body.price_cny = Number(priceCny);
    else body.price_cny = null;
    onSubmit(body);
  };

  return (
    <Modal title={title} onClose={onClose} size="lg">
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">姓名 / 称呼 *</label>
            <input
              className="input"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="张三"
            />
          </div>
          <div>
            <label className="label">客户 id（slug）{isEdit && "（不可改）"}</label>
            <input
              className="input"
              value={id}
              disabled={isEdit}
              onChange={(e) => setId(e.target.value)}
              placeholder="留空则自动按姓名生成"
            />
          </div>
          <div>
            <label className="label">渠道来源</label>
            <input
              className="input"
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
              placeholder="微信群 / 朋友介绍 / 公众号 …"
            />
          </div>
          <div>
            <label className="label">成交金额（CNY）</label>
            <input
              className="input"
              type="number"
              value={priceCny}
              step="0.01"
              onChange={(e) => setPriceCny(e.target.value)}
            />
          </div>
          <div>
            <label className="label">付款时间</label>
            <input
              className="input"
              type="date"
              value={paidAt}
              max={todayIso()}
              onChange={(e) => setPaidAt(e.target.value)}
            />
          </div>
          <div>
            <label className="label">硬件指纹码</label>
            <input
              className="input font-mono text-xs"
              value={fingerprint}
              onChange={(e) => setFingerprint(e.target.value)}
              placeholder="XXXXX-XXXXX-XXXXX-XXXXX-XXXXX-XXXXX"
            />
          </div>
        </div>
        <div>
          <label className="label">备注</label>
          <textarea
            className="textarea h-20"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        {!isEdit && (
          <ContactsEditor
            contactKinds={contactKinds}
            contacts={contacts}
            setContacts={setContacts}
          />
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose}>
            取消
          </button>
          <button type="submit" className="btn-primary">
            {isEdit ? "保存" : "创建"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function ContactsEditor({
  contactKinds,
  contacts,
  setContacts,
}: {
  contactKinds: string[];
  contacts: Contact[];
  setContacts: (c: Contact[]) => void;
}) {
  const [kind, setKind] = useState(contactKinds[0] ?? "wechat");
  const [value, setValue] = useState("");
  const [note, setNote] = useState("");

  const add = () => {
    if (!value.trim()) return;
    setContacts([...contacts, { kind, value: value.trim(), note: note.trim() }]);
    setValue("");
    setNote("");
  };

  return (
    <div>
      <label className="label">联系方式（创建后可继续增删）</label>
      <div className="grid grid-cols-[6rem_1fr_1fr_auto] gap-2">
        <select className="select" value={kind} onChange={(e) => setKind(e.target.value)}>
          {contactKinds.map((k) => (
            <option key={k} value={k}>
              {k}
            </option>
          ))}
        </select>
        <input
          className="input"
          placeholder="联系号码 / 账号"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
        <input
          className="input"
          placeholder="备注（可选）"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <button type="button" className="btn-secondary" onClick={add}>
          +
        </button>
      </div>
      {contacts.length > 0 && (
        <ul className="mt-2 divide-y divide-ink-100 rounded-md border border-ink-200 bg-ink-50">
          {contacts.map((c, i) => (
            <li
              key={`${c.kind}-${c.value}-${i}`}
              className="flex items-center justify-between gap-2 px-3 py-1.5 text-sm"
            >
              <div>
                <span className="font-mono text-xs text-ink-500">{c.kind}</span>{" "}
                <span>{c.value}</span>
                {c.note && <span className="ml-2 text-xs text-ink-500">（{c.note}）</span>}
              </div>
              <button
                type="button"
                className="text-ink-400 hover:text-red-600"
                onClick={() => setContacts(contacts.filter((_, j) => j !== i))}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
